import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { Checkbox, CheckboxGroup } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const PreferencesSection = ({ preferences, onUpdate }) => {
  const [formData, setFormData] = useState(preferences);
  const [hasChanges, setHasChanges] = useState(false);

  const bodyRegionOptions = [
    { value: 'head', label: 'Head & Neck' },
    { value: 'chest', label: 'Chest' },
    { value: 'abdomen', label: 'Abdomen & Pelvis' },
    { value: 'spine', label: 'Spine' },
    { value: 'musculoskeletal', label: 'Musculoskeletal' },
    { value: 'vascular', label: 'Vascular' }
  ];

  const modalityOptions = [
    { value: 'xray', label: 'X-Ray' },
    { value: 'ct', label: 'CT Scan' },
    { value: 'mri', label: 'MRI' },
    { value: 'ultrasound', label: 'Ultrasound' },
    { value: 'nuclear', label: 'Nuclear Medicine' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    onUpdate(formData);
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Clinical Preferences</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Customize your default settings and notification preferences
          </p>
        </div>
        {hasChanges && (
          <Button
            variant="default"
            iconName="Save"
            iconPosition="left"
            onClick={handleSave}
          >
            Save Changes
          </Button>
        )}
      </div>
      <div className="clinical-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Default Clinical Settings</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Default Body Region"
            description="Pre-select this region when creating new recommendations"
            options={bodyRegionOptions}
            value={formData?.defaultBodyRegion}
            onChange={(value) => handleChange('defaultBodyRegion', value)}
          />

          <Select
            label="Preferred Imaging Modality"
            description="Your most commonly used imaging type"
            options={modalityOptions}
            value={formData?.preferredModality}
            onChange={(value) => handleChange('preferredModality', value)}
          />
        </div>

        <div className="mt-6">
          <CheckboxGroup label="Emergency Alert Settings">
            <Checkbox
              label="Enable emergency case notifications"
              description="Receive immediate alerts for urgent imaging recommendations"
              checked={formData?.emergencyAlerts}
              onChange={(e) => handleChange('emergencyAlerts', e?.target?.checked)}
            />
            <Checkbox
              label="Highlight radiation warnings"
              description="Show prominent warnings for high-dose imaging procedures"
              checked={formData?.radiationWarnings}
              onChange={(e) => handleChange('radiationWarnings', e?.target?.checked)}
            />
            <Checkbox
              label="Auto-save recommendations"
              description="Automatically save recommendations to your history"
              checked={formData?.autoSave}
              onChange={(e) => handleChange('autoSave', e?.target?.checked)}
            />
          </CheckboxGroup>
        </div>
      </div>
      <div className="clinical-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Notification Preferences</h3>
        
        <div className="space-y-4">
          <div className="flex items-start justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Bell" size={20} color="var(--color-primary)" />
              <div>
                <h4 className="text-sm font-semibold text-foreground">Credit Alerts</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Get notified when your credits are running low
                </p>
              </div>
            </div>
            <Checkbox
              checked={formData?.notifications?.creditAlerts}
              onChange={(e) => handleChange('notifications', {
                ...formData?.notifications,
                creditAlerts: e?.target?.checked
              })}
            />
          </div>

          <div className="flex items-start justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
              <div>
                <h4 className="text-sm font-semibold text-foreground">Approval Status Updates</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Receive updates on account verification and approval status
                </p>
              </div>
            </div>
            <Checkbox
              checked={formData?.notifications?.approvalUpdates}
              onChange={(e) => handleChange('notifications', {
                ...formData?.notifications,
                approvalUpdates: e?.target?.checked
              })}
            />
          </div>

          <div className="flex items-start justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="TrendingUp" size={20} color="var(--color-accent)" />
              <div>
                <h4 className="text-sm font-semibold text-foreground">Usage Analytics</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Weekly summary of your recommendation usage and patterns
                </p>
              </div>
            </div>
            <Checkbox
              checked={formData?.notifications?.usageAnalytics}
              onChange={(e) => handleChange('notifications', {
                ...formData?.notifications,
                usageAnalytics: e?.target?.checked
              })}
            />
          </div>

          <div className="flex items-start justify-between p-4 bg-muted rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Sparkles" size={20} color="var(--color-warning)" />
              <div>
                <h4 className="text-sm font-semibold text-foreground">Feature Updates</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  Stay informed about new features and platform improvements
                </p>
              </div>
            </div>
            <Checkbox
              checked={formData?.notifications?.featureUpdates}
              onChange={(e) => handleChange('notifications', {
                ...formData?.notifications,
                featureUpdates: e?.target?.checked
              })}
            />
          </div>
        </div>
      </div>
      <div className="clinical-card p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Display Preferences</h3>
        
        <div className="space-y-4">
          <Checkbox
            label="Show evidence grades in recommendations"
            description="Display A/B/C evidence grading for each recommendation"
            checked={formData?.display?.showEvidenceGrades}
            onChange={(e) => handleChange('display', {
              ...formData?.display,
              showEvidenceGrades: e?.target?.checked
            })}
          />
          <Checkbox
            label="Display radiation dose calculations"
            description="Show estimated radiation exposure for imaging procedures"
            checked={formData?.display?.showRadiationDose}
            onChange={(e) => handleChange('display', {
              ...formData?.display,
              showRadiationDose: e?.target?.checked
            })}
          />
          <Checkbox
            label="Enable compact view"
            description="Use condensed layout for recommendation history"
            checked={formData?.display?.compactView}
            onChange={(e) => handleChange('display', {
              ...formData?.display,
              compactView: e?.target?.checked
            })}
          />
        </div>
      </div>
    </div>
  );
};

export default PreferencesSection;